<template>
  <span ref="el" class="nb-tooltip" @focusin="handleFocusIn" @focusout="handleFocusOut">
    <span v-if="!hasTooltipContent"><slot /></span>
    <span
      v-else-if="event === 'hover'"
      class="nb-tooltip-hover"
      @mouseenter="showTooltip"
      @mouseleave="scheduleHide"
      @mousemove="handleTooltipPlacement"
      ><slot
    /></span>
    <span v-else class="nb-tooltip-click" @click="handleTooltipToggle"><slot /></span>
    <div
      v-if="hasTooltipContent"
      :id="tooltipId"
      class="nb-tooltip-box"
      role="tooltip"
      @mouseenter="cancelHide"
      @mouseleave="scheduleHide"
    >
      <template v-if="text">{{ text }}</template>
      <slot v-else name="body" />
    </div>
  </span>
</template>

<script setup lang="ts">
import '../scss/components/nb-tooltip.scss'
import { ref, computed, onBeforeUnmount, useSlots } from 'vue'
import type { NbTooltips } from './NbTooltips'
const props = withDefaults(defineProps<NbTooltips>(), {
  text: '',
  event: 'hover',
  hideDelayMs: 1000
})
const slots = useSlots()
const hasTooltipContent = computed(() => !!props.text || !!slots.body)
const el = ref()
let hideTimer: ReturnType<typeof setTimeout> | null = null

const getBox = () => el.value?.querySelector('.nb-tooltip-box') as HTMLElement | null

const showTooltip = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
  const tooltipEl = getBox()
  if (!tooltipEl) return
  tooltipEl.classList.add('nb-tooltip-box-active')
  tooltipEl.style.pointerEvents = 'auto'
}

const scheduleHide = () => {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    const tooltipEl = getBox()
    if (tooltipEl) {
      tooltipEl.classList.remove('nb-tooltip-box-active')
      tooltipEl.style.pointerEvents = ''
    }
    hideTimer = null
  }, props.hideDelayMs)
}

const cancelHide = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

const handleFocusIn = () => {
  if (props.event === 'hover') showTooltip()
}

const handleFocusOut = () => {
  if (props.event === 'hover') scheduleHide()
}

onBeforeUnmount(() => {
  if (hideTimer) clearTimeout(hideTimer)
})

const handleTooltipToggle = (e: Event) => {
  if (e.target) {
    const tooltipEl = getBox()
    if (tooltipEl) tooltipEl.classList.toggle('nb-tooltip-box-active')
  }
}
const handleTooltipPlacement = (e: Event) => {
  if (!e.target) return
  const tooltipEl = el.value.querySelector('.nb-tooltip-box') as HTMLElement
  if (!tooltipEl) return

  const me = e as MouseEvent
  const wrapperRect = el.value.getBoundingClientRect()
  const cursorX = me.offsetX + wrapperRect.left
  const cursorY = me.offsetY + wrapperRect.top
  const storybookContainer = el.value.closest('.sb-story') as HTMLElement | null
  const containerRect = storybookContainer?.getBoundingClientRect()

  if (!props.position) {
    if (containerRect) {
      tooltipEl.style.top = cursorY - containerRect.top + 12 + 'px'
      tooltipEl.style.left = cursorX - containerRect.left + 12 + 'px'
    } else {
      tooltipEl.style.top = cursorY + 'px'
      tooltipEl.style.left = cursorX + 'px'
    }
    return
  }

  const tipRect = tooltipEl.getBoundingClientRect()
  const gap = 12
  let top = cursorY
  let left = cursorX
  switch (props.position) {
    case 'top':
      top = cursorY - tipRect.height - gap
      left = cursorX - tipRect.width / 2
      break
    case 'bottom':
      top = cursorY + gap
      left = cursorX - tipRect.width / 2
      break
    case 'left':
      top = cursorY - tipRect.height / 2
      left = cursorX - tipRect.width - gap
      break
    case 'right':
      top = cursorY - tipRect.height / 2
      left = cursorX + gap
      break
    case 'top-left':
      top = cursorY - tipRect.height - gap
      left = cursorX - tipRect.width - gap
      break
    case 'top-right':
      top = cursorY - tipRect.height - gap
      left = cursorX + gap
      break
    case 'bottom-left':
      top = cursorY + gap
      left = cursorX - tipRect.width - gap
      break
    case 'bottom-right':
      top = cursorY + gap
      left = cursorX + gap
      break
  }
  if (containerRect) {
    top -= containerRect.top
    left -= containerRect.left
  }
  tooltipEl.style.marginTop = '0'
  tooltipEl.style.top = top + 'px'
  tooltipEl.style.left = left + 'px'
}
</script>
